import { AmbulatoryContact } from 'domain/usecases/ambulatory/ambulatory-contact'
import { CreateAmbulatoryOrder } from 'domain/usecases/ambulatory/create-ambulatory-order'
import { GetAmbulatoryCids } from 'domain/usecases/ambulatory/get-ambulatory-cids'
import { GetAmbulatoryCounters } from 'domain/usecases/ambulatory/get-ambulatory-counters'
import { GetAmbulatoryMedications } from 'domain/usecases/ambulatory/get-ambulatory-medications'
import { GetAmbulatoryOrders } from 'domain/usecases/ambulatory/get-ambulatory-orders'
import { GetAmbulatorySchedulePanelOrganized } from 'domain/usecases/ambulatory/get-ambulatory-panel-organized'
import { GetAmbulatoryProcedures } from 'domain/usecases/ambulatory/get-ambulatory-procedures'
import { GetOneAmbulatoryOrder } from 'domain/usecases/ambulatory/get-one-ambulatory-order'
import { LoadAmbulatoryOrderDocument } from 'domain/usecases/ambulatory/load-ambulatory-order-document'
import { SearchAmbulatoryOrders } from 'domain/usecases/ambulatory/search-ambulatory-order'
import { SyncAmbulatoryToken } from 'domain/usecases/ambulatory/sync-ambulatory-token'
import { makeAutoObservable } from 'mobx'

type Services = {
  ambulatoryContact: AmbulatoryContact
  syncAmbulatoryToken: SyncAmbulatoryToken
  getAmbulatorySchedulePanelOrganized: GetAmbulatorySchedulePanelOrganized
  getAmbulatoryCounters: GetAmbulatoryCounters
  getAmbulatoryOrders: GetAmbulatoryOrders
  searchAmbulatoryOrders: SearchAmbulatoryOrders
  getOneAmbulatoryOrder: GetOneAmbulatoryOrder
  loadAmbulatoryOrderDocument: LoadAmbulatoryOrderDocument
  getAmbulatoryProcedures: GetAmbulatoryProcedures
  getAmbulatoryCids: GetAmbulatoryCids
  getAmbulatoryMedications: GetAmbulatoryMedications
  createAmbulatoryOrder: CreateAmbulatoryOrder
}

export class AmbulatoryService {
  constructor(private readonly services: Services) {
    makeAutoObservable(this)
  }

  async ambulatoryContact(
    params: AmbulatoryContact.Params,
    patient_id: number,
    ambulatory_schedule_id: number
  ): Promise<AmbulatoryContact.Model> {
    return this.services.ambulatoryContact.contact(
      params,
      patient_id,
      ambulatory_schedule_id
    )
  }

  async syncAmbulatoryToken(
    params: SyncAmbulatoryToken.Params
  ): Promise<SyncAmbulatoryToken.Model> {
    return this.services.syncAmbulatoryToken.sync(params)
  }

  async getAmbulatorySchedulePanelOrganized(
    params: GetAmbulatorySchedulePanelOrganized.Params
  ): Promise<GetAmbulatorySchedulePanelOrganized.Model> {
    return this.services.getAmbulatorySchedulePanelOrganized.get(params)
  }

  async getAmbulatoryCounters(
    doctor_id: number
  ): Promise<GetAmbulatoryCounters.Model> {
    return this.services.getAmbulatoryCounters.get(doctor_id)
  }

  async getAmbulatoryOrders(
    params: GetAmbulatoryOrders.Params
  ): Promise<GetAmbulatoryOrders.Model> {
    return this.services.getAmbulatoryOrders.get(params)
  }

  async getOneAmbulatoryOrder(
    params: GetOneAmbulatoryOrder.Params
  ): Promise<GetOneAmbulatoryOrder.Model> {
    return this.services.getOneAmbulatoryOrder.get(params)
  }

  async searchAmbulatoryOrders(
    params: SearchAmbulatoryOrders.Params
  ): Promise<SearchAmbulatoryOrders.Model> {
    return this.services.searchAmbulatoryOrders.search(params)
  }

  async loadAmbulatoryOrderDocument(
    params: LoadAmbulatoryOrderDocument.Params
  ): Promise<LoadAmbulatoryOrderDocument.Model> {
    return this.services.loadAmbulatoryOrderDocument.load(params)
  }

  async getAmbulatoryProcedures(
    params: GetAmbulatoryProcedures.Params
  ): Promise<GetAmbulatoryProcedures.Model> {
    return this.services.getAmbulatoryProcedures.get(params)
  }

  async getAmbulatoryCids(
    params: GetAmbulatoryCids.Params
  ): Promise<GetAmbulatoryCids.Model> {
    return this.services.getAmbulatoryCids.get(params)
  }

  async getAmbulatoryMedications(
    params: GetAmbulatoryMedications.Params
  ): Promise<GetAmbulatoryMedications.Model> {
    return this.services.getAmbulatoryMedications.get(params)
  }

  async createAmbulatoryOrder(
    params: CreateAmbulatoryOrder.Params
  ): Promise<CreateAmbulatoryOrder.Model> {
    return this.services.createAmbulatoryOrder.create(params)
  }
}

export default AmbulatoryService
